import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { STRIPE_PUBLIC_KEY } from '../../../config';
import { publicSetupPaymentMethod } from '@openapi/commission-groups/commission-groups';

const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

function SetupForm({ commissionGroupName }) {
    const stripe = useStripe();
    const elements = useElements();
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setLoading(true);
        const { error } = await stripe.confirmSetup({
            elements,
            confirmParams: {
                return_url: window.location.href + '?done=1',
            },
        });
        if (error) {
            setStatus({ type: 'error', message: error.message });
        }
        setLoading(false);
    };

    return (
        <form onSubmit={handleSubmit}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                Add Payment Method for {commissionGroupName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add a card or bank account to enable automatic invoice payments.
            </Typography>
            <PaymentElement />
            {status && (
                <Alert severity={status.type} sx={{ mt: 2 }}>
                    {status.message}
                </Alert>
            )}
            <Box sx={{ mt: 2 }}>
                <button
                    type="submit"
                    disabled={!stripe || loading}
                    style={{
                        padding: '10px 24px',
                        fontSize: 16,
                        cursor: loading ? 'wait' : 'pointer',
                    }}
                >
                    {loading ? 'Saving...' : 'Save Payment Method'}
                </button>
            </Box>
        </form>
    );
}

function SetupPaymentPage() {
    const { stripeCustomerId, hash } = useParams();
    const [clientSecret, setClientSecret] = useState(null);
    const [commissionGroupName, setCommissionGroupName] = useState('');
    const [error, setError] = useState(null);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (window.location.search.includes('done=1')) {
            setDone(true);
            return;
        }

        publicSetupPaymentMethod(stripeCustomerId, hash)
            .then((data) => {
                if (data.clientSecret) {
                    setClientSecret(data.clientSecret);
                    setCommissionGroupName(data.commissionGroupName || '');
                } else {
                    setError('Invalid or expired link');
                }
            })
            .catch(() => setError('Failed to load payment setup'));
    }, [stripeCustomerId, hash]);

    if (done) {
        return (
            <Box sx={{ maxWidth: 500, mx: 'auto', mt: 8, p: 3 }}>
                <Alert severity="success">
                    Payment method saved successfully. You can close this page.
                </Alert>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ maxWidth: 500, mx: 'auto', mt: 8, p: 3 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    if (!clientSecret) {
        return (
            <Box sx={{ maxWidth: 500, mx: 'auto', mt: 8, p: 3, textAlign: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 500, mx: 'auto', mt: 8, p: 3 }}>
            <Elements stripe={stripePromise} options={{ clientSecret }}>
                <SetupForm commissionGroupName={commissionGroupName} />
            </Elements>
        </Box>
    );
}

export default SetupPaymentPage;
